import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { referenceAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { toArray } from '../utils/apiData';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const sortByYearDesc = (items = []) =>
  [...items].sort((a, b) => (b.tax_year || 0) - (a.tax_year || 0));

const emptyForm = { tax_year: '', rate_per_mile: '' };

const AdminMileageRatesPage = () => {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rates, setRates] = useState([]);
  const [error, setError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [dialogError, setDialogError] = useState('');

  const loadData = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await referenceAPI.getMileageRates();
      setRates(sortByYearDesc(toArray(res.data, 'mileage rates')));
    } catch (err) {
      console.error('Error loading mileage rates:', err);
      setError(err.response?.data?.error || 'Failed to load mileage rates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenAdd = () => {
    setSelectedRate(null);
    setFormData(emptyForm);
    setDialogError('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (rate) => {
    setSelectedRate(rate);
    setFormData({
      tax_year: String(rate.tax_year),
      rate_per_mile: String(rate.rate_per_mile),
    });
    setDialogError('');
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedRate(null);
    setFormData(emptyForm);
    setDialogError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const taxYear = Number(formData.tax_year);
    const rate = Number(formData.rate_per_mile);

    if (!Number.isInteger(taxYear) || taxYear < 1900 || taxYear > 2200) {
      setDialogError('Enter a valid tax year');
      return;
    }
    if (Number.isNaN(rate) || rate <= 0) {
      setDialogError('Enter a rate per mile greater than 0');
      return;
    }

    setSaving(true);
    setDialogError('');
    try {
      if (selectedRate) {
        await referenceAPI.updateMileageRate(selectedRate.id, {
          tax_year: taxYear,
          rate_per_mile: rate,
        });
      } else {
        await referenceAPI.createMileageRate({
          tax_year: taxYear,
          rate_per_mile: rate,
        });
      }
      handleClose();
      loadData();
    } catch (err) {
      console.error('Error saving mileage rate:', err);
      setDialogError(err.response?.data?.error || 'Failed to save mileage rate');
    } finally {
      setSaving(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteConfirm = async () => {
    try {
      await referenceAPI.deleteMileageRate(deleteTarget.id);
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      console.error('Error deleting mileage rate:', err);
      notify.error(errorMessage(err, 'Failed to delete mileage rate'));
    }
  };

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            alignItems: 'center',
            gap: { xs: 1.5, sm: 0 },
            mb: 3,
          }}
        >
          <Typography variant="h4">Mileage Rates</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{
              minWidth: { xs: 0, sm: 'auto' },
              width: { xs: 32, sm: 'auto' },
              height: { xs: 32, sm: 'auto' },
              px: { xs: 0, sm: 2 },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Add Mileage Rate
            </Box>
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: error ? 1 : 2 }}>
          The IRS standard mileage rate for each tax year. The Tax Year Workbook uses the
          rate matching the year being exported to turn computed mileage into a dollar
          deduction; a year with no rate here gets no mileage line at all.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
                <TableCell>Tax Year</TableCell>
                <TableCell>Rate per Mile</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableSkeletonRows columns={3} />}
              {rates.map((rate) => (
                <TableRow key={rate.id}>
                  <TableCell align="center" sx={actionsKabobCellSx}>
                    <RowActionsMenu
                      actions={[
                        { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenEdit(rate) },
                        { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => setDeleteTarget(rate), color: 'error' },
                      ]}
                    />
                  </TableCell>
                  <TableCell>{rate.tax_year}</TableCell>
                  <TableCell>${Number(rate.rate_per_mile).toFixed(3)}</TableCell>
                </TableRow>
              ))}
              {!loading && rates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No mileage rates found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedRate ? 'Edit Mileage Rate' : 'Add Mileage Rate'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
            <TextField
              size="small"
              name="tax_year"
              label="Tax Year"
              type="number"
              value={formData.tax_year}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              size="small"
              name="rate_per_mile"
              label="Rate per Mile ($)"
              type="number"
              inputProps={{ min: 0, step: 0.001 }}
              value={formData.rate_per_mile}
              onChange={handleChange}
              required
              fullWidth
              placeholder="0.670"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Mileage Rate"
        message={`Permanently delete the mileage rate for ${deleteTarget?.tax_year}? This cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        confirmLabel="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

export default AdminMileageRatesPage;
